import { useState, useRef } from "react"
import { supabase } from "@/integrations/supabase/client"
import { Button } from "@/components/ui/button"
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog"
import { Upload, Camera, Trash2, Crop } from "lucide-react"
import { PhotoCropDialog } from "@/components/PhotoCropDialog"
import { toast } from "sonner"
import { compressImage } from "@/lib/imageCompression"
import { invalidateProfiles } from "@/lib/socialBatch"

interface ProfilePictureUploadProps {
  userId: string
  currentAvatarUrl?: string | null
  firstName: string
  onAvatarUpdate: (newAvatarUrl: string | null) => void
  dialogClassName?: string
  column?: "avatar_url" | "business_avatar_url"
  inline?: boolean
}

export const ProfilePictureUpload = ({ 
  userId, 
  currentAvatarUrl, 
  firstName, 
  onAvatarUpdate,
  dialogClassName = "",
  column = "avatar_url",
  inline = false,
}: ProfilePictureUploadProps) => {
  const [isUploading, setIsUploading] = useState(false)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [cropSrc, setCropSrc] = useState<string | null>(null)

  const closeCrop = () => {
    if (cropSrc?.startsWith("blob:")) URL.revokeObjectURL(cropSrc)
    setCropSrc(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    // Validate file type (some Android pickers leave the type empty)
    const looksLikeImage = file.type.startsWith('image/') || (!file.type && /\.(jpe?g|png|webp|gif|heic|heif)$/i.test(file.name))
    if (!looksLikeImage) {
      toast.error("Please select an image file")
      if (fileInputRef.current) fileInputRef.current.value = ''
      return
    }

    // Phone photos are often large; we crop and compress before upload.
    if (file.size > 20 * 1024 * 1024) {
      toast.error("Image must be less than 20MB")
      if (fileInputRef.current) fileInputRef.current.value = ''
      return
    }

    setCropSrc(URL.createObjectURL(file))
  }

  const uploadFile = async (file: File) => {
    setIsUploading(true)

    try {
      const processedFile = await compressImage(file, "avatar")

      // Delete old avatar if exists
      if (currentAvatarUrl) {
        const oldPath = currentAvatarUrl.split('/').pop()
        if (oldPath) {
          await supabase.storage
            .from('avatars')
            .remove([`${userId}/${oldPath}`])
        }
      }

      // Upload new avatar
      const fileExt = processedFile.name.split('.').pop()
      const fileName = `avatar-${Date.now()}.${fileExt}`
      const filePath = `${userId}/${fileName}`

      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, processedFile, { cacheControl: '31536000' })

      if (uploadError) throw uploadError

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath)

      // Update profile
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ [column]: publicUrl } as any)
        .eq('id', userId)

      if (updateError) throw updateError
      invalidateProfiles()

      onAvatarUpdate(publicUrl)
      closeCrop()
      setIsDialogOpen(false)
      toast.success("Profile picture updated successfully!")

    } catch (error: any) {
      console.error('Upload error:', error)
      toast.error("Failed to upload profile picture")
    } finally {
      setIsUploading(false)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  const handleRemoveAvatar = async () => {
    if (!currentAvatarUrl) return

    setIsUploading(true)

    try {
      // Delete from storage
      const path = currentAvatarUrl.split('/').pop()
      if (path) {
        await supabase.storage
          .from('avatars')
          .remove([`${userId}/${path}`])
      }

      // Update profile
      const { error } = await supabase
        .from('profiles')
        .update({ [column]: null } as any)
        .eq('id', userId)

      if (error) throw error
      invalidateProfiles()

      onAvatarUpdate(null)
      setIsDialogOpen(false)
      toast.success("Profile picture removed")

    } catch (error: any) {
      console.error('Remove error:', error)
      toast.error("Failed to remove profile picture")
    } finally {
      setIsUploading(false)
    }
  }

  const getInitials = (name: string) => {
    return name.charAt(0).toUpperCase()
  }

  if (inline) {
    return (
      <div className="flex flex-col items-center space-y-4">
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={isUploading}
          className="relative group cursor-pointer"
          aria-label="Upload a photo"
        >
          <Avatar className="w-28 h-28 rounded-2xl border-4 border-border">
            <AvatarImage src={currentAvatarUrl || undefined} alt={firstName} className="rounded-2xl object-cover" />
            <AvatarFallback className="bg-gradient-warm text-white text-3xl font-semibold rounded-2xl">
              {getInitials(firstName)}
            </AvatarFallback>
          </Avatar>
          <div className="absolute inset-0 bg-black/40 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
            <Camera className="h-6 w-6 text-white" />
          </div>
        </button>
        <Button
          onClick={() => fileInputRef.current?.click()}
          disabled={isUploading}
          variant="outline"
          className="w-full max-w-xs"
        >
          <Upload className="mr-2 h-4 w-4" />
          {isUploading ? "Uploading..." : currentAvatarUrl ? "Change photo" : "Choose a photo"}
        </Button>
{currentAvatarUrl && (
          <Button type="button" onClick={() => setCropSrc(currentAvatarUrl)} disabled={isUploading} variant="outline" className="w-full max-w-xs">
            <Crop className="mr-2 h-4 w-4" />
            Adjust photo
          </Button>
        )}
        {currentAvatarUrl && (
          <Button
            onClick={handleRemoveAvatar}
            disabled={isUploading}
            variant="ghost"
            className="text-muted-foreground"
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Remove photo
          </Button>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          className="hidden"
        />
        <p className="text-xs text-muted-foreground text-center">
          JPG or PNG, max 20MB.
        </p>
        <PhotoCropDialog open={!!cropSrc} imageSrc={cropSrc} onCancel={closeCrop} onConfirm={uploadFile} saving={isUploading} />
      </div>
    )
  }

  return (
    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      <DialogTrigger asChild>
        <div className="relative group cursor-pointer">
          <Avatar className="w-16 h-16 rounded-xl">
            <AvatarImage src={currentAvatarUrl || undefined} alt={firstName} className="rounded-xl object-cover" />
            <AvatarFallback className="bg-gradient-warm text-white text-lg font-semibold rounded-xl">
              {getInitials(firstName)}
            </AvatarFallback>
          </Avatar>
          <div className="absolute inset-0 bg-black/40 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
            <Camera className="h-5 w-5 text-white" />
          </div>
        </div>
      </DialogTrigger>
      
      <DialogContent className={`sm:max-w-md ${dialogClassName}`}>
        <DialogHeader>
          <DialogTitle>Update Profile Picture</DialogTitle>
        </DialogHeader>
        
        <div className="flex flex-col items-center space-y-6">
          <Avatar className="w-32 h-32 border-4 border-border">
            <AvatarImage src={currentAvatarUrl || undefined} alt={firstName} />
            <AvatarFallback className="bg-gradient-warm text-white text-3xl font-semibold">
              {getInitials(firstName)}
            </AvatarFallback>
          </Avatar>

          <div className="flex flex-col space-y-3 w-full">
            <Button 
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
              className="w-full"
            >
              <Upload className="mr-2 h-4 w-4" />
              {isUploading ? "Uploading..." : "Upload New Picture"}
            </Button>

{currentAvatarUrl && (
          <Button type="button" onClick={() => setCropSrc(currentAvatarUrl)} disabled={isUploading} variant="outline" className="w-full">
            <Crop className="mr-2 h-4 w-4" />
            Adjust photo
          </Button>
        )}
        {currentAvatarUrl && (
              <Button 
                onClick={handleRemoveAvatar}
                disabled={isUploading}
                variant="outline"
                className="w-full"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Remove Picture
              </Button>
            )}
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
          />

          <p className="text-xs text-muted-foreground text-center">
            Supported formats: JPG, PNG. Max size: 20MB.
          </p>
        </div>
        <PhotoCropDialog open={!!cropSrc} imageSrc={cropSrc} onCancel={closeCrop} onConfirm={uploadFile} saving={isUploading} />
      </DialogContent>
    </Dialog>
  )
}